import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import ChatWidget from '../components/ChatWidget';
import SubscriptionGuard from '../components/SubscriptionGuard';
import PaywallGuard from '../components/PaywallGuard';
import OnboardingTour from '../components/OnboardingTour';
import FirstTimeLogoPrompt from '../components/FirstTimeLogoPrompt';
import NotificationBar from '../components/NotificationBar';
import { Sidebar } from './admin/Sidebar';
import { Topbar } from './admin/Topbar';
import { MobileNav } from './admin/MobileNav';
import { matchItem } from './admin/navConfig';
import { applyTheme, getTheme } from '../lib/theme';

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [badges, setBadges] = useState({});
  const [projects, setProjects] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [companyName, setCompanyName] = useState('');

  useEffect(() => {
    applyTheme(getTheme());
    return () => document.documentElement.classList.remove('dark');
  }, []);

  useEffect(() => {
    let mounted = true;
    const fetchBadges = () => api.getNavBadges().then((r) => mounted && setBadges(r.data || {})).catch(() => {});
    fetchBadges();
    api.getCompany().then((r) => mounted && setCompanyName(r.data?.name || '')).catch(() => {});
    const interval = setInterval(fetchBadges, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  useEffect(() => {
    api.getProjects().then((r) => setProjects((r.data || []).filter((p) => !p.archived))).catch(() => {});
    api.getProjectSummaries().then((r) => setSummaries(r.data?.summaries || {})).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const current = matchItem(location.pathname);
    if (!current?.badgeKey || !(badges[current.badgeKey] > 0)) return;
    setBadges((b) => ({ ...b, [current.badgeKey]: 0 }));
    api.markNavBadgeSeen(current.badgeKey).catch(() => {});
  }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isActive = (path) => (path === '/dashboard' ? location.pathname === '/dashboard' : location.pathname === path || location.pathname.startsWith(path + '/'));
  const activeProjectId = (location.pathname.match(/\/dashboard\/projects\/([^/]+)/) || [])[1];
  const recent = [...projects].sort((a, b) => String(b.updated_at || b.created_at || '').localeCompare(String(a.updated_at || a.created_at || ''))).slice(0, 4);

  return (
    <PaywallGuard>
    <SubscriptionGuard>
    <div className="flex h-screen bg-[#F4F2EE]">
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <Sidebar open={sidebarOpen} collapsed={collapsed} setCollapsed={setCollapsed} onClose={() => setSidebarOpen(false)}
        isActive={isActive} badges={badges} recent={recent} summaries={summaries} activeProjectId={activeProjectId} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <Topbar title={matchItem(location.pathname)?.label || 'Dashboard'} onOpenSidebar={() => setSidebarOpen(true)}
          badges={badges} user={user} companyName={companyName} onLogout={handleLogout} projects={projects} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 native-scroll pb-24 lg:pb-20" data-testid="admin-main">
          <Outlet />
        </main>
      </div>
      <MobileNav pathname={location.pathname} onMore={() => setSidebarOpen(true)} />
      <ChatWidget />
      <NotificationBar />
      <OnboardingTour />
      <FirstTimeLogoPrompt />
    </div>
    </SubscriptionGuard>
    </PaywallGuard>
  );
};

export default AdminLayout;
